import {
  FunctionComponent,
  ReactNode,
  useState,
  useCallback,
  useRef,
} from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Box, Inline, Pressable, Text, xcss } from '@atlaskit/primitives';
import Lozenge from '@atlaskit/lozenge';
import Popup from '@atlaskit/popup';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
  betaLabel: {
    id: 'appUpdates.aiDisclaimer.beta',
    defaultMessage: 'Beta',
    description: 'Beta label shown next to AI-generated content',
  },
  tooltipText: {
    id: 'appUpdates.aiDisclaimer.tooltipText',
    defaultMessage: 'Uses AI. Verify results.',
    description:
      'Disclaimer body text shown in the tooltip next to AI-generated content (Beta lozenge prepended visually)',
  },
});

const wrapperStyles = xcss({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'space.050',
});

const triggerStyles = xcss({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'space.050',
  cursor: 'default',
  color: 'color.text',
  backgroundColor: 'color.background.neutral.subtle',
  padding: 'space.0',
  borderStyle: 'none',
});

interface AIDisclaimerProps {
  /**
   * Optional label rendered after the AI icon + Beta lozenge. Used inline
   * inside table/column headers and detail summary labels.
   */
  label?: ReactNode;
}

/**
 * AIDisclaimer
 *
 * Compact, always-visible indicator for AI-generated content. Shows an AI
 * sparkle icon and a "Beta" lozenge next to a label. Hovering reveals a
 * tooltip with the full disclaimer ("Beta. Uses AI. Verify results.").
 *
 * Renders: `[AI icon] [Beta] {label}`
 *
 * See: https://hello.atlassian.net/wiki/spaces/CDSTD/pages/4575298584
 */
export const AIDisclaimer: FunctionComponent<AIDisclaimerProps> = ({
  label,
}) => {
  const { formatMessage } = useIntl();
  const betaLabel = formatMessage(messages.betaLabel);
  const tooltipBody = formatMessage(messages.tooltipText);

  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  }, []);

  return (
    <Inline xcss={wrapperStyles} testId="ai-disclaimer">
      {label}
      <Popup
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        placement="top"
        content={() => (
          <Box
            padding="space.100"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <Inline space="space.075" alignBlock="center">
              <Lozenge appearance="default" isBold={false}>
                {betaLabel}
              </Lozenge>
              <Text size="small">{tooltipBody}</Text>
            </Inline>
          </Box>
        )}
        trigger={(triggerProps) => (
          <Pressable
            {...triggerProps}
            testId="ai-disclaimer-trigger"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={() => setIsOpen((prev) => !prev)}
            xcss={triggerStyles}
          >
            <AtlassianIntelligenceIcon
              label={`${betaLabel}. ${tooltipBody}`}
              color={token('color.icon.subtle')}
              spacing="spacious"
            />
            <Lozenge appearance="default" isBold={false}>
              {betaLabel}
            </Lozenge>
          </Pressable>
        )}
      />
    </Inline>
  );
};

export default AIDisclaimer;
